import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2, Layers, AlertCircle, CheckCircle, Edit2 } from 'lucide-react';
import { PListLineDto, OrderBatchNoDto } from '../../../../../types/picking';
import { pickingService } from '../../../../../services/pickingService';
import { ScanTextInput, NumericInput } from '../../../../common/inputs';

interface ExtendedPickingLine extends PListLineDto {
  itemDescription?: string;
}

interface BatchNumberModalProps {
  isOpen: boolean;
  line: ExtendedPickingLine | null;
  pListId: number;
  onClose: () => void;
  onBatchNumbersChanged: () => void;
}

export const BatchNumberModal: React.FC<BatchNumberModalProps> = ({
  isOpen,
  line,
  pListId,
  onClose,
  onBatchNumbersChanged
}) => {
  const [batchNumbers, setBatchNumbers] = useState<OrderBatchNoDto[]>([]);
  const [newBatchNo, setNewBatchNo] = useState('');
  const [newQuantity, setNewQuantity] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editQuantity, setEditQuantity] = useState('');
  const batchInputRef = useRef<HTMLInputElement>(null);

  // Load batch numbers when modal opens
  useEffect(() => {
    if (isOpen && line) {
      loadBatchNumbers();
      setNewBatchNo('');
      setNewQuantity('');
      setError(null);
      setEditingId(null);
    }
  }, [isOpen, line]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && batchInputRef.current) {
      setTimeout(() => batchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const loadBatchNumbers = async () => {
    if (!line) return;

    setLoading(true);
    try {
      const data = await pickingService.getBatchNumbers(pListId, line.orderNo, line.lineNo);
      setBatchNumbers(data);
    } catch (err) {
      console.error('Error loading batch numbers:', err);
      // Use the ones from the line if available
      setBatchNumbers(line.batchNumbers || []);
    } finally {
      setLoading(false);
    }
  };

  const getTotalRegisteredQuantity = () => {
    return batchNumbers.reduce((sum, b) => sum + b.quantity, 0);
  };

  const getRemainingQuantity = () => {
    if (!line) return 0;
    return line.qty - getTotalRegisteredQuantity();
  };

  const handleAddBatchNumber = async () => {
    if (!line || !newBatchNo.trim() || !newQuantity) return;

    const qty = parseFloat(newQuantity);
    if (isNaN(qty) || qty <= 0) {
      setError('Antall m\u00e5 v\u00e6re st\u00f8rre enn 0');
      return;
    }

    const remaining = getRemainingQuantity();
    if (qty > remaining) {
      setError(`Antall kan ikke overstige gjenstående (${remaining})`);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const created = await pickingService.createBatchNumber(
        pListId,
        line.orderNo,
        line.lineNo,
        line.pListLineNo,
        newBatchNo.trim(),
        qty
      );
      setBatchNumbers([...batchNumbers, created]);
      setNewBatchNo('');
      setNewQuantity('');
      onBatchNumbersChanged();
      batchInputRef.current?.focus();
    } catch (err: any) {
      setError(err.message || 'Kunne ikke lagre batchnummer');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateBatchNumber = async (batchId: number) => {
    const qty = parseFloat(editQuantity);
    if (isNaN(qty) || qty <= 0) {
      setError('Antall m\u00e5 v\u00e6re st\u00f8rre enn 0');
      return;
    }

    // Calculate remaining excluding this batch
    const otherBatchesTotal = batchNumbers
      .filter(b => b.id !== batchId)
      .reduce((sum, b) => sum + b.quantity, 0);
    const maxQty = (line?.qty || 0) - otherBatchesTotal;

    if (qty > maxQty) {
      setError(`Antall kan ikke overstige ${maxQty}`);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updated = await pickingService.updateBatchNumber(pListId, batchId, qty);
      setBatchNumbers(batchNumbers.map(b => b.id === batchId ? updated : b));
      setEditingId(null);
      setEditQuantity('');
      onBatchNumbersChanged();
    } catch (err: any) {
      setError(err.message || 'Kunne ikke oppdatere batchnummer');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBatchNumber = async (batchId: number) => {
    setSaving(true);
    setError(null);

    try {
      await pickingService.deleteBatchNumber(pListId, batchId);
      setBatchNumbers(batchNumbers.filter(b => b.id !== batchId));
      onBatchNumbersChanged();
    } catch (err: any) {
      setError(err.message || 'Kunne ikke slette batchnummer');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newBatchNo.trim() && newQuantity) {
      handleAddBatchNumber();
    } else if (e.key === 'Escape') {
      if (editingId) {
        setEditingId(null);
        setEditQuantity('');
      } else {
        onClose();
      }
    }
  };

  const startEditing = (batch: OrderBatchNoDto) => {
    setEditingId(batch.id);
    setEditQuantity(batch.quantity.toString());
  };

  if (!isOpen || !line) return null;

  const totalRegistered = getTotalRegisteredQuantity();
  const requiredQty = line.qty;
  const remaining = getRemainingQuantity();
  const isComplete = remaining <= 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-lg sm:max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Layers className="text-purple-600" size={24} />
            <h3 className="text-lg font-semibold">Registrer batchnummer</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        {/* Item info */}
        <div className="p-4 bg-gray-50 border-b">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-500">Varenr:</span>
              <span className="ml-2 font-medium">{line.itemNo}</span>
            </div>
            <div>
              <span className="text-gray-500">Antall:</span>
              <span className="ml-2 font-medium">{line.qty}</span>
            </div>
          </div>
          {line.itemDescription && (
            <div className="mt-1 text-sm text-gray-600 truncate">
              {line.itemDescription}
            </div>
          )}
        </div>

        {/* Progress indicator */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              Registrert: {totalRegistered} / {requiredQty}
            </span>
            {isComplete ? (
              <span className="flex items-center gap-1 text-green-600 text-sm">
                <CheckCircle size={16} />
                Komplett
              </span>
            ) : (
              <span className="text-orange-600 text-sm">
                Gjenstår: {remaining}
              </span>
            )}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${isComplete ? 'bg-green-500' : 'bg-purple-500'}`}
              style={{ width: `${Math.min((totalRegistered / requiredQty) * 100, 100)}%` }}
            />
          </div>
        </div>

        {/* Input area */}
        <div className="p-4 border-b">
          <div className="flex gap-2">
            <ScanTextInput
              ref={batchInputRef}
              value={newBatchNo}
              onChange={(e) => setNewBatchNo(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Batchnummer..."
              className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              disabled={saving || isComplete}
            />
            <NumericInput
              mode="decimal"
              value={newQuantity}
              onChange={(e) => setNewQuantity(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Antall"
              min="0.01"
              max={remaining}
              className="w-24 px-3 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center"
              disabled={saving || isComplete}
            />
            <button
              onClick={handleAddBatchNumber}
              disabled={!newBatchNo.trim() || !newQuantity || saving || isComplete}
              className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Plus size={20} />
            </button>
          </div>

          {error && (
            <div className="mt-2 flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle size={16} />
              {error}
            </div>
          )}
        </div>

        {/* Batch numbers list */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center text-gray-500 py-8">
              Laster batchnummer...
            </div>
          ) : batchNumbers.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              Ingen batchnummer registrert enn\u00e5
            </div>
          ) : (
            <div className="space-y-2">
              {batchNumbers.map((batch) => (
                <div
                  key={batch.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                >
                  <div className="flex-1">
                    <div className="font-mono font-medium">{batch.batchNo}</div>
                    {editingId === batch.id ? (
                      <div className="flex items-center gap-2 mt-1">
                        <NumericInput
                          mode="decimal"
                          value={editQuantity}
                          onChange={(e) => setEditQuantity(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleUpdateBatchNumber(batch.id);
                            } else if (e.key === 'Escape') {
                              setEditingId(null);
                              setEditQuantity('');
                            }
                          }}
                          className="w-20 px-2 py-1 border rounded text-sm"
                          autoFocus
                        />
                        <button
                          onClick={() => handleUpdateBatchNumber(batch.id)}
                          className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                        >
                          Lagre
                        </button>
                        <button
                          onClick={() => {
                            setEditingId(null);
                            setEditQuantity('');
                          }}
                          className="px-2 py-1 bg-gray-400 text-white text-xs rounded hover:bg-gray-500"
                        >
                          Avbryt
                        </button>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600 mt-1">
                        Antall: <span className="font-semibold">{batch.quantity}</span>
                      </div>
                    )}
                  </div>
                  {editingId !== batch.id && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => startEditing(batch)}
                        disabled={saving}
                        className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                        title="Rediger antall"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteBatchNumber(batch.id)}
                        disabled={saving}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                        title="Slett batchnummer"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
          >
            Lukk
          </button>
        </div>
      </div>
    </div>
  );
};
